/**
 * Workspace note — per-workspace encrypted knowledge notes with citations.
 * The firm's practice profile / house style / precedent knowhow lives here and is
 * read by grounded agents. All endpoints require an OPEN workspace.
 * Backend: `/v1/facts` (src/api/v1/routers/notes.py).
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData } from '../errors.js'

export type FactResponse = components['schemas']['FactResponse']
export type CreateFactRequest = components['schemas']['CreateFactRequest']
export type UpdateFactRequest = components['schemas']['UpdateFactRequest']
export type FactSearchRequest = components['schemas']['FactSearchRequest']
export type FactSearchResponse = components['schemas']['FactSearchResponse']

/** Create a note. `content` is required; `shared` makes it workspace-wide. */
export async function createFact(arbi: ArbiClient, body: CreateFactRequest): Promise<FactResponse> {
  return requireData(await arbi.fetch.POST('/v1/facts/', { body }), 'Failed to create note')
}

/** List the open workspace's notes (newest first). */
/**
 * List notes. Passing a date range — or `datedOnly` — turns this into the
 * TIMELINE: only dated notes, ordered by when they happened rather than when
 * they were written. Each bound is read at its own precision and widened
 * outward, so `to: '2019'` runs through 31 December.
 *
 * The range selects by overlap, so a note spanning all of 2019 shows up in a
 * one-month window it covers.
 */
export async function listFacts(
  arbi: ArbiClient,
  options?: { pinnedOnly?: boolean; from?: string; to?: string; datedOnly?: boolean }
): Promise<FactResponse[]> {
  return requireData(
    await arbi.fetch.GET('/v1/facts/list', {
      params: {
        query: {
          pinned_only: options?.pinnedOnly ?? false,
          date_from: options?.from,
          date_to: options?.to,
          dated_only: options?.datedOnly,
        },
      },
    }),
    'Failed to list notes'
  )
}

/** Semantic/keyword/hybrid search across the workspace's notes. */
export async function searchFacts(
  arbi: ArbiClient,
  body: FactSearchRequest
): Promise<FactSearchResponse> {
  return requireData(await arbi.fetch.POST('/v1/facts/search', { body }), 'Failed to search notes')
}

export async function updateFact(
  arbi: ArbiClient,
  factId: string,
  body: UpdateFactRequest
): Promise<FactResponse> {
  return requireData(
    await arbi.fetch.PATCH('/v1/facts/{fact_ext_id}', {
      params: { path: { fact_ext_id: factId } },
      body,
    }),
    'Failed to update note'
  )
}

export async function deleteFact(arbi: ArbiClient, factId: string): Promise<void> {
  const { error } = await arbi.fetch.DELETE('/v1/facts/{fact_ext_id}', {
    params: { path: { fact_ext_id: factId } },
  })
  if (error) throw new Error('Failed to delete note')
}

/**
 * Set a one-off reminder on a dated note. Give `remindAt` for an explicit time,
 * or `leadMinutes` to fire that long before the note's date; with neither it
 * fires when the note's span starts. A reminder whose time has already passed
 * is rejected rather than silently never firing.
 */
export async function remindFact(
  arbi: ArbiClient,
  factId: string,
  options: { remindAt?: string; leadMinutes?: number } = {}
): Promise<FactResponse> {
  return requireData(
    await arbi.fetch.POST('/v1/facts/{fact_ext_id}/remind', {
      params: { path: { fact_ext_id: factId } },
      body: {
        remind_at: options.remindAt ?? null,
        lead_minutes: options.leadMinutes ?? null,
      },
    }),
    'Failed to set reminder'
  )
}
